import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import AlignLeftOutlined from '@ant-design/icons/lib/icons/AlignLeftOutlined';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Layout from 'antd/lib/layout';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import React, { useCallback, useContext, useState } from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { CloudVenLogsModal } from 'src/components/modals/CloudVenLogsModal/CloudVenLogsModal';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import { useAuth } from 'src/domain/auth/useAuth';
import { AbilityContext } from '../../../components/ability/can';
import { AuditTrailModal } from '../../../components/modals/AuditTrailModal/AuditTrailModal';
import { PageFooter } from '../../../components/PageFooter';
import { PageHeader } from '../../../components/PageHeader';
import { SearchInput } from '../../../components/searchInput/searchInput';
import { CloudVenDispatchTypeTableLabelMap } from '../../../components/selectors/CloudVenDispatchByMethodSelect/CloudVenDispatchTypeMethodSelect';
import { ActionList } from '../../../components/table/actionList/actionList';
import { getColumnSortOrder } from '../../../components/table/columnFormatHelpers';
import { CustomizableColumnType, CustomizableTable } from '../../../components/table/CustomizableTable';
import { CustomizableTableContextProvider } from '../../../components/table/CustomizableTableContext';
import { useDocumentTitle } from '../../../components/useDocumentTitle';
import { FORM_MODE, useFormMode } from '../../../components/useFormMode';
import { VenDispatchType } from '../../../components/VenDispatchType/VenDispatchType';
import { VenIcon } from '../../../components/VenIcon/VenIcon';
import { IView } from '../../../components/viewSwitcher/viewInterface';
import { ViewSwitcher } from '../../../components/viewSwitcher/ViewSwitcher';
import { AuditEntityType } from '../../../domain/audit-trail/interface';
import { DEFAULT_PAGINATION, PAGE_ACTION } from '../../../domain/commonConst';
import { IPaginationMeta } from '../../../domain/IPagination';
import { validatePagination } from '../../../domain/validatePagination';
import { deleteCloudVen, exportCloudVens } from '../../../domain/ven';
import { ICloudVen } from '../../../domain/ven/interface';
import { useCloudVenListQuery } from '../../../domain/ven/queries';
import { toUsefulParams } from '../../toUsefulParams';
import { usePageLocation } from '../../usePageState';
import { CloudVenSidebar } from '../cloudVenSidebar/CloudVenSidebar';
import { useCloudVenSidebar } from '../cloudVenSidebar/useCloudVenSidebar';
import { CloudVenFormModal } from '../components/CloudVenFormModal';
import { venStatusFilter, viewOptions } from './venViewInterface';

export const CloudVenView = () => {
    useDocumentTitle('Cloud VENs');

    const { notification, modal } = App.useApp();
    const ability = useContext(AbilityContext);
    const auth = useAuth()!;
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);

    const [cloudVen, setCloudVen] = useState<ICloudVen | Partial<ICloudVen>>();
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [venAction, setVenAction] = useState<PAGE_ACTION | ''>();

    const { onSetFormMode, formMode } = useFormMode();
    const { view, handleViewMode }: IView = useOutletContext();

    const listQuery = {
        pagination: pageState?.pagination ?? DEFAULT_PAGINATION,
        hideRelatedToClosedSites: true,
        ...(pageState?.sorter && { sorter: pageState.sorter }),
        ...(pageState?.search && { search: pageState.search }),
        ...(pageState?.filter && { filter: pageState.filter }),
    };

    const {
        data,
        isFetching,
        refetch: refetchCloudVens,
    } = useCloudVenListQuery(listQuery, {
        keepPreviousData: true,
    });
    const cloudVens = data?.data || [];
    const meta: IPaginationMeta = {
        total: data?.meta?.total ?? 0,
        offset: data?.meta?.offset ?? 0,
        limit: data?.meta?.limit ?? pageState?.pagination?.pageSize ?? DEFAULT_PAGINATION.pageSize,
    };

    const handleSearch = useCallback(
        (value: string) => {
            if (value === pageState.search) {
                setPageQuery({
                    ...pageState,
                    pagination: { ...pageState.pagination, current: DEFAULT_PAGINATION.current },
                });
                return;
            }

            setPageQuery({ ...pageState, search: value });
        },
        [pageState, setPageQuery]
    );

    async function onTableChange(pagination: any, filter: any, sorter: any) {
        const preparedParams = toUsefulParams({
            pagination,
            sorter,
            filter: {
                ...pageState.filter,
                ...filter,
            },
        });

        setPageQuery({
            ...pageState,
            ...preparedParams,
        });
    }

    async function exportCloudVensAction() {
        try {
            setExportLoading(true);

            const params = {
                ...toUsefulParams(pageState),
                hideRelatedToClosedSites: true,
            };

            await exportCloudVens(params);
        } catch (err: any) {
            notification.error({ key: 'cloud-ven-export-error', message: err.message || 'Cannot export cloud VENs!' });
        }
        setExportLoading(false);
    }

    function deleteCloudVenWithConfirm(selectedVen: ICloudVen) {
        return () => {
            modal.confirm({
                icon: <ExclamationCircleOutlined />,
                content: `Deleting cloud VEN ${selectedVen.ven_username || ''}. Are you sure?`,
                onOk: async () => {
                    await deleteCloudVenAction(selectedVen);
                },
                onCancel() {
                    console.log('Cancel');
                },
            });
        };
    }

    async function deleteCloudVenAction(selectedVen: ICloudVen) {
        try {
            await deleteCloudVen(selectedVen);

            const updatedTotal = meta.total - 1;
            const validPagination = validatePagination(pageState.pagination, updatedTotal);

            setPageQuery({ ...pageState, pagination: validPagination });
            await refetchCloudVens({ ...pageState, pagination: validPagination });

            notification.info({ key: 'cloud-ven-delete-info', message: 'Cloud VEN deleted' });
        } catch (err: any) {
            notification.error({ key: 'cloud-ven-delete-error', message: err.message || 'Cannot delete VEN!' });
        }
    }

    const showNewVenCloudModal = () => {
        onSetFormMode(FORM_MODE.NEW);
        setCloudVen({
            ven_username: undefined,
            ven_password: undefined,
            ven_id: undefined,
            ven_uri: undefined,
            ven_vtn_id: undefined,
            ven_cert: undefined,
            ven_default_event_signal: undefined,
            ven_key: undefined,
            ven_profile: '2.0a',
            ven_registration_id: undefined,
        });
    };

    function showEditVenModal(record: ICloudVen) {
        return () => {
            onSetFormMode(FORM_MODE.EDIT);
            setCloudVen(record);
        };
    }

    async function onVenModalClose(selectedVen: ICloudVen) {
        setCloudVen(undefined);
        onSetFormMode('');

        if (selectedVen) {
            await refetchCloudVens(listQuery);
        }
    }

    function showAuditTrailModal(record: ICloudVen) {
        return () => {
            setVenAction(PAGE_ACTION.AUDIT_TRAIL);
            setCloudVen(record);
        };
    }

    function showLogsModal(record: ICloudVen) {
        return () => {
            setVenAction(PAGE_ACTION.SHOW_LOGS);
            setCloudVen(record);
        };
    }

    const actions = [
        ...(ability.can('update', 'CloudVen')
            ? [
                  {
                      title: 'Edit',
                      onClick: (record: ICloudVen) => showEditVenModal(record),
                  },
              ]
            : []),
        ...(ability.can('delete', 'CloudVen')
            ? [
                  {
                      title: 'Delete',
                      onClick: (record: ICloudVen) => deleteCloudVenWithConfirm(record),
                  },
              ]
            : []),
        ...(auth.user?.isAdminRoleType()
            ? [
                  {
                      title: 'Show Logs',
                      icon: <AlignLeftOutlined />,
                      onClick: (record: ICloudVen) => showLogsModal(record),
                  },
              ]
            : []),
        ...(auth.user?.isAdminRoleType()
            ? [
                  {
                      title: 'Audit Trail',
                      icon: <HistoryOutlined />,
                      onClick: (record: ICloudVen) => showAuditTrailModal(record),
                  },
              ]
            : []),
    ];

    const { isFiltersSidebarEnabled, sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useCloudVenSidebar();

    const handleFieldChange = (field: string) => (value: any) => {
        setPageQuery({
            ...pageState,
            filter: {
                ...pageState.filter,
                [field]: value,
            },
        });
    };

    const cloudVenColumns: CustomizableColumnType<ICloudVen>[] = [
        {
            key: 'ven_icon',
            title: 'Status',
            width: 70,
            render: (type, record) => {
                return <VenIcon ven={record} />;
            },
        },
        {
            key: 'ven_username',
            title: 'Username',
            dataIndex: 'ven_username',
            width: 100,
            sorter: (a, b) => a.ven_username.localeCompare(b.ven_username),
            ...getColumnSortOrder('ven_username', pageState.sorter),
        },
        {
            key: 'ven_profile',
            title: 'Profile',
            dataIndex: 'ven_profile',
            width: 100,
            sorter: (a, b) => a.ven_profile.localeCompare(b.ven_profile),
            ...getColumnSortOrder('ven_profile', pageState.sorter),
        },
        {
            key: 'ven_id',
            title: 'VEN ID',
            dataIndex: 'ven_id',
            width: 100,
            sorter: (a, b) => a.ven_id.localeCompare(b.ven_id),
            ...getColumnSortOrder('ven_id', pageState.sorter),
        },
        {
            key: 'ven_vtn_id',
            title: 'VTN ID',
            dataIndex: 'ven_vtn_id',
            width: 100,
            sorter: (a, b) => a.ven_vtn_id.localeCompare(b.ven_vtn_id),
            ...getColumnSortOrder('ven_vtn_id', pageState.sorter),
        },
        {
            key: 'ven_market_context',
            title: 'Market context',
            dataIndex: 'ven_market_context',
            width: 100,
        },
        {
            key: 'dispatch_type',
            title: 'Dispatch Type',
            dataIndex: 'dispatch_type',
            width: 100,
            render: (text: string) => CloudVenDispatchTypeTableLabelMap[text] || text,
        },
        {
            key: 'dispatch_target',
            title: 'Dispatch Target',
            dataIndex: 'dispatch_type',
            width: 150,
            render: (text: string, record: ICloudVen) => <VenDispatchType cloudVen={record} />,
        },
        {
            key: 'action',
            sorter: false,
            width: 90,
            render: (text: string, record) => <ActionList actions={actions} item={record} />,
        },
    ];

    return (
        <CustomizableTableContextProvider configKey="cloudVen" columns={cloudVenColumns}>
            <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                {isFiltersSidebarEnabled && (
                    <CloudVenSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={collapsed => setSidebarCollapsed(collapsed)}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                        totalRecords={meta.total}
                    />
                )}

                <Layout.Content style={{ padding: '0 26px' }}>
                    <PageHeader
                        pageTitle="VEN"
                        extra={[
                            <ViewSwitcher viewOptions={viewOptions(ability)} key="ven-view-switcher" view={view} handleViewMode={handleViewMode} />,
                        ]}
                        actions={[
                            !isFiltersSidebarEnabled && (
                                <Space key="cloud-ven-filter">
                                    <Typography.Text>VEN status:</Typography.Text>
                                    <Select
                                        size="large"
                                        value={pageState?.filter?.status ?? ''}
                                        onChange={handleFieldChange('status')}
                                        style={{ width: '110px' }}
                                        options={[
                                            {
                                                label: 'All',
                                                value: '',
                                            },
                                            ...venStatusFilter,
                                        ]}
                                    />
                                </Space>
                            ),
                            <SearchInput key="cloud-ven-search" onSearch={handleSearch} defaultValue={pageState?.search} />,
                            <Button
                                size="large"
                                key="download-csv"
                                onClick={exportCloudVensAction}
                                icon={<DownloadOutlined />}
                                loading={exportLoading}
                            >
                                Download CSV
                            </Button>,
                            ability.can('create', 'CloudVen') && (
                                <Button size="large" key="new-cloud-ven" type="primary" onClick={showNewVenCloudModal} icon={<PlusCircleOutlined />}>
                                    New VEN
                                </Button>
                            ),
                        ]}
                    />

                    <CustomizableTable<ICloudVen>
                        size="small"
                        rowKey="id"
                        className="cloud-ven-table"
                        sticky={{ offsetHeader: 62 }}
                        showHiddenColumnsCount={auth?.user?.isAdminRoleType()}
                        dataSource={cloudVens}
                        loading={isFetching}
                        onChange={onTableChange}
                        meta={meta}
                    />

                    {cloudVen && (formMode === FORM_MODE.EDIT || formMode === FORM_MODE.NEW) && (
                        <CloudVenFormModal ven={cloudVen} onClose={onVenModalClose} />
                    )}

                    {cloudVen && venAction === PAGE_ACTION.AUDIT_TRAIL && (
                        <AuditTrailModal onClose={() => setVenAction('')} entityId={cloudVen.id!} entityType={AuditEntityType.CLOUD_VEN} />
                    )}

                    {cloudVen && venAction === PAGE_ACTION.SHOW_LOGS && <CloudVenLogsModal onClose={() => setVenAction('')} ven={cloudVen} />}
                    <PageFooter />
                </Layout.Content>
            </Layout>
        </CustomizableTableContextProvider>
    );
};
